import React from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  CheckCircle,
  Pending,
  Cancel,
  ContentCopy,
  Visibility,
  Phone,
  AccessTime,
  AttachMoney
} from '@mui/icons-material';

const MobileCards = ({
  transactions,
  onTransactionClick,
  onCopyPixCode
}) => {
  const formatCurrency = (value) => {
    if (!value) return value;
    return parseFloat(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return phoneNumber;
    const masks = {
      '+55': { name: 'Brasil', mask: '(xx) xxxxx-xxxx' },
      '+54': { name: 'Argentina', mask: '(x) xxxx xx-xxxx' },
      '+595': { name: 'Paraguai', mask: '(xxx) xxx-xxx' },
      '+1': { name: 'EUA', mask: '(xxx) xxx-xxxx' }
    };

    let ddi = Object.keys(masks).find(ddi => phoneNumber.startsWith(ddi));
    if (!ddi) return phoneNumber;

    phoneNumber = phoneNumber.replace(ddi, '');
    const mask = masks[ddi].mask;
    const numericPhoneNumber = phoneNumber.replace(/\D/g, '');

    let formattedPhoneNumber = '';
    let maskIndex = 0;
    for (let i = 0; i < mask.length && maskIndex < numericPhoneNumber.length; i++) {
      if (mask[i] === 'x') {
        formattedPhoneNumber += numericPhoneNumber[maskIndex];
        maskIndex++;
      } else {
        formattedPhoneNumber += mask[i];
      }
    }

    return ddi + ' ' + formattedPhoneNumber;
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'Pendente':
        return {
          color: '#2196F3',
          bgColor: '#E3F2FD',
          icon: <Pending sx={{ fontSize: 16 }} />,
          label: 'Pendente'
        };
      case 'PAGO':
        return {
          color: '#4CAF50',
          bgColor: '#E8F5E8',
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
          label: 'Pago'
        };
      case 'Concluído':
        return {
          color: '#4CAF50',
          bgColor: '#E8F5E8',
          icon: <CheckCircle sx={{ fontSize: 16 }} />,
          label: 'Concluído'
        };
      case 'Falhou':
      case 'Expirado':
        return {
          color: '#F44336',
          bgColor: '#FFEBEE',
          icon: <Cancel sx={{ fontSize: 16 }} />,
          label: status
        };
      default:
        return {
          color: '#757575',
          bgColor: '#F5F5F5',
          icon: <Cancel sx={{ fontSize: 16 }} />,
          label: status
        };
    }
  };

  return (
    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
      {transactions.map((transaction) => {
        const statusConfig = getStatusConfig(transaction.status);
        return (
          <Card
            key={transaction._id}
            elevation={2}
            sx={{
              width: '100%',
              marginBottom: 2,
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                cursor: 'pointer'
              }
            }}
            onClick={() => onTransactionClick(transaction)}
          >
            <CardContent sx={{ padding: 3 }}>
              {/* Header com status e valor */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Chip
                  icon={statusConfig.icon}
                  label={statusConfig.label}
                  sx={{
                    backgroundColor: statusConfig.bgColor,
                    color: statusConfig.color,
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    height: 32,
                    '& .MuiChip-icon': {
                      color: statusConfig.color
                    }
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 'bold',
                    color: '#2E7D32',
                    fontSize: '1.1rem'
                  }}
                >
                  {formatCurrency(transaction.amount)}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Informações principais */}
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'bold',
                    color: '#1976D2',
                    mb: 1,
                    fontSize: '1rem'
                  }}
                >
                  {transaction.nomeCompleto || '-'}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    mb: 1,
                    fontSize: '0.9rem'
                  }}
                >
                  {transaction.description || '-'}
                </Typography>

                {/* Informações do usuário */}
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 1,
                  flexWrap: 'wrap'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', fontWeight: 'bold' }}>
                      IDPDV:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1976D2', fontSize: '0.8rem', fontWeight: 'bold' }}>
                      {transaction.createdBy?.idpdv || '-'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem', fontWeight: 'bold' }}>
                      Usuário:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1976D2', fontSize: '0.8rem', fontWeight: 'bold' }}>
                      {transaction.createdBy?.fullname || '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Informações secundárias */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone sx={{ fontSize: 16, color: '#666' }} />
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                    {formatPhoneNumber('+' + transaction.wppNumber)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime sx={{ fontSize: 16, color: '#666' }} />
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                    {transaction.date ? new Date(transaction.date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : '-'}
                  </Typography>
                </Box>
              </Box>

              {/* Ações */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1
              }}>
                <Tooltip title="Ver detalhes">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onTransactionClick(transaction);
                    }}
                    sx={{
                      color: '#1976D2',
                      backgroundColor: '#E3F2FD',
                      '&:hover': { backgroundColor: '#BBDEFB' }
                    }}
                  >
                    <Visibility sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>

                {transaction.status === 'Pendente' && (
                  <Tooltip title="Copiar código PIX">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopyPixCode(transaction.pixCopyPaste);
                      }}
                      sx={{
                        color: '#4CAF50',
                        backgroundColor: '#E8F5E8',
                        '&:hover': { backgroundColor: '#C8E6C9' }
                      }}
                    >
                      <ContentCopy sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
};

export default MobileCards;
